import React, { useState, useRef } from 'react';
import _merge from 'lodash/merge';
import { getContext, useCombinedRefs } from 'common';
import { AgGridReact } from '@ag-grid-community/react';
import type { GridOptions, Module } from '@ag-grid-community/all-modules';
import * as ColumnTypesModule from './ColumnTypes';
import * as defaultFrameworkComponents from './FrameworkComponents';
import { tabToNextCell, navigateToNextCell, handleCellKeyDown, handleHeaderNavigation } from './KeyboardNavigation';
import { handleCellPointerDown } from './PointerEvent';
import { hasFocusableContent, focusCellRenderer, CONSTANTS } from './GridHelpers';
import DefaultIcons from './Icons';
import styled, { ThemedGlobalCCFKGridStyle } from './styles';

const { defaultColDef, ...defaultColumnTypes } = ColumnTypesModule;

const { COMPACT_ROW_HEIGHT, DEFAULT_ROW_HEIGHT } = CONSTANTS;

export interface DataGridProps extends GridOptions, React.ComponentPropsWithRef<'div'> {
  /* Props just for the wrapper */
  wrapperProps?: React.ComponentPropsWithRef<'div'>;
  /** Row selection configuration supports 'single' or 'multiple'. */
  rowSelection?: string;
  /* Objects containing properties that column definitions can inherit. */
  columnTypes?: {};
  /** Contains column properties all columns will inherit. */
  defaultColDef?: {};
  /* Specify custom components that can be used by the grid */
  frameworkComponents?: {};
  /** If set to true, row selection won't happen when rows are clicked.
   * Use when you want checkbox selection exclusively.
   * Also should be used while using editable cells. */
  suppressRowClickSelection?: boolean;
  /** Set to true to render the grid in variant mode. */
  variant?: 'default' | 'compact';
  /** Custom icons of AG-Grid. Please check this AG-Grid doc: https://www.ag-grid.com/javascript-grid/custom-icons/#set-the-icons-through-gridoptions-javascript */
  icons?: {};
  /** To override the default context menu */
  getContextMenuItems?: any;
  /** Custom postProcessPopup callback */
  postProcessPopup?(...args: unknown[]): unknown;
  /** Custom onCellFocused callback */
  onCellFocused?(...args: unknown[]): unknown;
  /** Custom onCellKeyDown callback */
  onCellKeyDown?(...args: unknown[]): unknown;
  /** Custom onCellClicked callback */
  onCellClicked?(...args: unknown[]): unknown;
  /** Custom onCellEditingStarted callback */
  onCellEditingStarted?(...args: unknown[]): unknown;
  /** To override the theme. Please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** A flag for enabling the ThemedGlobalCCFKGridStyle inserted in DataGrid.
   * If this prop is enabled, ThemedGlobalCCFKGridStyle is required to be imported and inserted in the App.
   * It is a performance consideration and that this is recommended in cases where an application has multiple grids.
   * Please read Readme for more details.
   */
  disableAutoloadStyles?: boolean;
  /** If true, ccfk custom single cell copy functionality will be disabled. Ag-grid default copy strategy will be used. */
  disableSingleCellCopy?: boolean;
  /** To enable master detail, read https://www.ag-grid.com/javascript-data-grid/master-detail/ */
  masterDetail?: boolean;
  /** To register all or particular modules. See hhttps://www.ag-grid.com/react-data-grid/modules/ */
  modules?: Module[];
}

/** DataGrid is a data table component that allows you to list and manage data in columns and rows that can be sorted and filtered.
 * It is based off of Ag-Grid, for further information on Ag-Grid and its' documentation visit this link https://www.ag-grid.com/documentation-main/documentation.php.
 * Any other props than listed will be provided to the root `AgGridReact` component.
 */
const DataGrid = React.forwardRef<HTMLElement, DataGridProps>((props: DataGridProps, ref) =>
  getContext(({ RTL, theme }) => {
    const {
      rowSelection,
      columnTypes,
      defaultColDef: userProvidedDefaultColDef,
      getContextMenuItems,
      frameworkComponents,
      suppressRowClickSelection: userProvidedSuppressRowClickSelection,
      icons,
      variant,
      postProcessPopup,
      onCellFocused,
      onCellKeyDown,
      onCellClicked,
      wrapperProps,
      onCellEditingStarted,
      disableAutoloadStyles,
      masterDetail,
      disableSingleCellCopy,
      theme: propTheme,
      modules,
      ...otherProps
    } = props;
    const resolvedTheme = _merge({}, theme.DATAGRID, propTheme);
    const pastePromise = useRef(null);
    const gridRef = useRef(null);
    const combinedGridRef = useCombinedRefs(gridRef, ref);
    const [suppressRowClickSelection, setSuppressRowClickSelection] = useState(userProvidedSuppressRowClickSelection);

    // If column has focusable content, the whole row won't be selected
    const updateSuppressRowClickSelection = (column) => {
      if (column && hasFocusableContent(column)) {
        setSuppressRowClickSelection(true);
      } else {
        setSuppressRowClickSelection(false);
      }
    };

    // This is for make column menu align with bottom of header row
    const updateColumnMenuPosition = (params) => {
      if (params.type !== 'columnMenu') {
        return;
      }
      const { ePopup } = params;

      // hide column menu before the calculation is done
      ePopup.style.opacity = 0;

      /**
       * master detail grid may have short height where the column menu is long.
       *  so the column menu will be cut off at the bottom and scroll doesn't work properly since we move it down.
       *  In this case we need to shrink the height of column menu.
       */

      // setTimeout is for get correct popup height by using getBoundingClientRect
      setTimeout(() => {
        const size = params.column.gridApi.getSizesForCurrentTheme();
        const closestRoot = ePopup.closest('.ag-root-wrapper');
        if (closestRoot) {
          const rootHeight = closestRoot.getBoundingClientRect().height;
          const popupHeight = ePopup.getBoundingClientRect().height;

          // check if the distance from bottom of column menu to top of DataGrid is larger than DataGrid height
          // if so shrink the height of column menu
          if (popupHeight + size.headerHeight > rootHeight) {
            ePopup.style.height = `${rootHeight - size.headerHeight}px`;
          }
        }
        // check how many rows in header (exclude floating filters as they stay at bottom row)
        const boundingRect = params.eventSource.getBoundingClientRect();
        const rowHeight = params.column.gridApi.context.compactMode ? COMPACT_ROW_HEIGHT : DEFAULT_ROW_HEIGHT;
        const closestRootTop = closestRoot ? closestRoot.getBoundingClientRect().top : 0;
        // eslint-disable-next-line no-mixed-operators
        const top = boundingRect.top - closestRootTop + (rowHeight + boundingRect.height) / 2;
        ePopup.style.top = `${top}px`;
        ePopup.style.opacity = 1;
      });
    };

    const setPastePromise = (promise) => {
      pastePromise.current = promise;
    };

    const isCompact = variant === 'compact';

    return (
      <styled.GridWrapper className="ag-theme-material-csf" $theme={resolvedTheme} masterDetail={masterDetail} {...wrapperProps}>
        {!disableAutoloadStyles && <ThemedGlobalCCFKGridStyle />}
        <AgGridReact
          // @ts-expect-error, resolve after upgrading ag-grid
          ref={combinedGridRef}
          enableRtl={RTL}
          rowSelection={rowSelection}
          columnTypes={{ ...defaultColumnTypes, ...columnTypes }}
          defaultColDef={{ ...defaultColDef, ...userProvidedDefaultColDef }}
          getContextMenuItems={getContextMenuItems}
          frameworkComponents={{ ...defaultFrameworkComponents, ...frameworkComponents }}
          icons={{ ...DefaultIcons, ...icons }}
          rowHeight={isCompact ? COMPACT_ROW_HEIGHT : DEFAULT_ROW_HEIGHT}
          headerHeight={isCompact ? COMPACT_ROW_HEIGHT : DEFAULT_ROW_HEIGHT}
          floatingFiltersHeight={isCompact ? COMPACT_ROW_HEIGHT : DEFAULT_ROW_HEIGHT}
          groupHeaderHeight={isCompact ? COMPACT_ROW_HEIGHT : DEFAULT_ROW_HEIGHT}
          suppressRowClickSelection={suppressRowClickSelection}
          suppressChangeDetection
          tabToNextCell={params => tabToNextCell(params, gridRef.current)}
          navigateToNextCell={navigateToNextCell}
          navigateToNextHeader={params => handleHeaderNavigation(params, gridRef.current)}
          tabToNextHeader={params => handleHeaderNavigation(params, gridRef.current)}
          postProcessPopup={(params) => {
            postProcessPopup && postProcessPopup(params);
            updateColumnMenuPosition(params);
          }}
          onCellEditingStarted={(params) => {
            onCellEditingStarted && onCellEditingStarted(params);

            // proceed pasting when editable cell is editing (input field is focused)
            if (pastePromise.current) {
              pastePromise.current.then((callback) => {
                callback();
                setPastePromise(null);
              });
            }
          }}
          onCellClicked={(params) => {
            setTimeout(() => handleCellPointerDown(params));
            onCellClicked && onCellClicked(params);
          }}
          onCellFocused={(params) => {
            focusCellRenderer(params);
            !userProvidedSuppressRowClickSelection && updateSuppressRowClickSelection(params.column);
            onCellFocused && onCellFocused(params);
          }}
          onCellKeyDown={(params) => {
            handleCellKeyDown({ ...params, disableSingleCellCopy }, setPastePromise);
            onCellKeyDown && onCellKeyDown(params);
          }}
          masterDetail={masterDetail}
          modules={modules}
          {...otherProps}
        />
      </styled.GridWrapper>
    );
  }));

DataGrid.displayName = 'DataGrid';

DataGrid.defaultProps = {
  wrapperProps: {},
  rowSelection: 'multiple',
  columnTypes: {},
  defaultColDef: {},
  frameworkComponents: {},
  suppressRowClickSelection: false,
  variant: 'default',
  icons: {},
  postProcessPopup: undefined,
  onCellFocused: undefined,
  onCellKeyDown: undefined,
  onCellClicked: undefined,
  onCellEditingStarted: undefined,
  theme: {},
  disableAutoloadStyles: false,
  disableSingleCellCopy: false,
  masterDetail: false,
  getContextMenuItems: undefined,
  modules: undefined
};

export default DataGrid;
